/**
 * Labstep
 *
 * @module core/Modal/Wizard
 * @desc Wizard modal
 */

import classNames from 'classnames';
import { ModalWizardContext } from 'labstep-web/contexts/modal-wizard';
import { ModalConfirmation } from 'labstep-web/core/Modal/Confirmation';
import { ModalWizardHeader } from 'labstep-web/core/Modal/Wizard/Header';
import { ModalWizardStep } from 'labstep-web/core/Modal/Wizard/Step';
import { useModalRoute } from 'labstep-web/hooks/modalRoute';
import React, { useCallback, useMemo, useState } from 'react';
import Modal from '..';
import styles from './styles.module.scss';
import type { IModalWizardProps } from './types';

export const ModalWizard = <TMode extends 'mini' | 'fullscreen'>({
  routeId,
  steps,
  title,
  onFinish: onFinishProp,
  mode,
  onClose: onCloseProp,
  trigger,
  startIndex = 0,
  children,
  modalConfirmationProps,
  className,
  closeIcon = true,
  ...rest
}: IModalWizardProps<TMode>) => {
  const { open, toggleModal } = useModalRoute(routeId);
  const [activeStepIndex, setActiveStepIndex] = useState(startIndex);
  const [errorMessages, setErrorMessages] = React.useState<string[]>(
    [],
  );
  const [showConfirmationModal, setShowConfirmationModal] =
    useState(false);

  const resetConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const reset = useCallback(() => {
    setActiveStepIndex(0);
    setErrorMessages([]);
    toggleModal();
    resetConfirmationModal();
  }, [
    setActiveStepIndex,
    setErrorMessages,
    toggleModal,
    resetConfirmationModal,
  ]);

  const onClose = useCallback(() => {
    onCloseProp?.();
    reset();
  }, [onCloseProp, reset]);

  const onCloseWithConfirmation = useCallback(() => {
    if (modalConfirmationProps) {
      setShowConfirmationModal(true);
    } else {
      onClose();
    }
  }, [modalConfirmationProps, onClose]);

  const onFinish = useCallback(() => {
    onFinishProp?.();
    onClose();
  }, [onFinishProp, onClose]);

  const isLastStep = useMemo(() => {
    return activeStepIndex === steps.length - 1;
  }, [activeStepIndex, steps.length]);

  const goBack = useCallback(() => {
    setActiveStepIndex(activeStepIndex - 1);
  }, [activeStepIndex, setActiveStepIndex]);

  const goForward = useCallback(
    (onContinue) => {
      const fn = () => {
        if (isLastStep) {
          onFinish?.();
        } else {
          setActiveStepIndex(activeStepIndex + 1);
        }
        setErrorMessages([]);
      };
      if (onContinue) {
        onContinue({
          goForward: fn,
          goBack,
          setErrorMessages,
          reset,
        });
      } else {
        fn();
      }
    },
    [
      activeStepIndex,
      setActiveStepIndex,
      onFinish,
      isLastStep,
      goBack,
    ],
  );

  const value = useMemo(
    () => ({
      activeStepIndex,
      setActiveStepIndex,
      goBack,
      goForward,
      startIndex,
      isLastStep,
      errorMessages,
      steps,
      setErrorMessages,
    }),
    [
      activeStepIndex,
      setActiveStepIndex,
      goBack,
      goForward,
      startIndex,
      isLastStep,
      errorMessages,
      steps,
      setErrorMessages,
    ],
  );

  return (
    <>
      <ModalConfirmation
        {...modalConfirmationProps}
        open={showConfirmationModal}
        onClose={resetConfirmationModal}
        onCancel={resetConfirmationModal}
        onConfirm={() => {
          modalConfirmationProps?.onConfirm?.({
            toggleModal: onClose,
          });
        }}
      />
      <Modal
        className={classNames(className, {
          [styles.mini]: mode === 'mini',
        })}
        size={mode === 'fullscreen' ? mode : 'large'}
        closeIcon={closeIcon}
        open={open}
        onClose={onCloseWithConfirmation}
        trigger={trigger?.({ toggleModal })}
        {...rest}
      >
        <ModalWizardContext.Provider value={value}>
          <ModalWizardHeader
            title={title}
            steps={steps}
            activeStepIndex={activeStepIndex}
            mode={mode}
          />
          {children}
        </ModalWizardContext.Provider>
      </Modal>
    </>
  );
};

ModalWizard.Step = ModalWizardStep;
